import { computed, type ComputedRef } from "vue";
import { useRoute } from "vue-router";
import { RouteNames } from "@/router/app.router";
import { useChannelStore } from "@/stores/channel.store";

export interface ChannelShellRouteVisibility {
  showCommunitiesRow: ComputedRef<boolean>;
  hideNavigation: ComputedRef<boolean>;
  showProfileRow: ComputedRef<boolean>;
}

/**
 * Route-name-driven visibility for the channel shell's chrome: the
 * communities row, the primary navigation, and the profile row. Both
 * channel-layout-v2.vue and channel-layout-legacy.vue mount the same set of
 * routes behind the same nav/profile-row rules, so this is the single source
 * of truth both shells consume.
 */
export function useChannelShellRouteVisibility(): ChannelShellRouteVisibility {
  const route = useRoute();
  const channelStore = useChannelStore();

  const showCommunitiesRow = computed(
    () =>
      (route.name === RouteNames.Channel &&
        channelStore.hasMultipleCommunities) ||
      channelStore.loadingChannel,
  );

  const hideNavigation = computed(() => {
    switch (route.name) {
      case RouteNames.NewShow:
      case RouteNames.ThreadsNew:
      case RouteNames.ThreadsItem:
      case RouteNames.Profile:
      case RouteNames.ProfileEdit:
      case RouteNames.ProfileNotifications:
      case RouteNames.ProfilePrivacy:
      case RouteNames.ProfileMedia:
      case RouteNames.ProfileEvents:
      case RouteNames.ProfilePurchases:
        return true;
      default:
        return false;
    }
  });

  const showProfileRow = computed(() => {
    switch (route.name) {
      case RouteNames.Profile:
      case RouteNames.ProfileMembership:
      case RouteNames.ProfileEdit:
      case RouteNames.ProfileNotifications:
      case RouteNames.ProfilePrivacy:
      case RouteNames.ProfileMedia:
      case RouteNames.ProfileEvents:
      case RouteNames.ProfilePurchases:
        return false;
      default:
        return true;
    }
  });

  return { showCommunitiesRow, hideNavigation, showProfileRow };
}
